import type { ReactElement, ReactNode, Ref } from 'react'
import { View } from 'react-native'

type TListboxCommonProps = {
  topContent?: ReactNode
  bottomContent?: ReactNode
  className?: string
  ref?: Ref<View>
}
export type TListboxDynamicProps<T extends object> = TListboxCommonProps & {
  children?: (item: T, index: number) => ReactNode
  items: T[]
}

type TListboxStaticProps = TListboxCommonProps & {
  children: Exclude<ReactNode, VoidFunction>
}

type TListboxProps<T extends object = TNoMoreProps> = TListboxDynamicProps<T> | TListboxStaticProps

function renderListbox(props: TListboxStaticProps): ReactElement
function renderListbox<T extends object>(props: TListboxDynamicProps<T>): ReactElement
function renderListbox({ ref, className, children, topContent, bottomContent, ...otherProps }: TListboxProps) {
  return (
    <View className={className} ref={ref}>
      {topContent}
      {typeof children === 'function' && 'items' in otherProps && otherProps.items.map(children)}
      {typeof children != 'function' && children}
      {bottomContent}
    </View>
  )
}

/**
 * Either a static listbox or a dynamic listbox.
 *
 * @example
 * ```tsx
 * <Listbox>
 *   <ListboxItem>One</ListboxItem>
 *   <ListboxItem>Two</ListboxItem>
 * </Listbox>
 * ```
 *
 * @example
 * ```tsx
 * <Listbox items={[{ id: 1, name: 'One' }, { id: 2, name: 'Two' }]}>
 *   {(item, index) => <ListboxItem key={item.id}>{item.name}</ListboxItem>}
 * </Listbox>
 * ```
 */
// oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- public-API typing
export const Listbox = renderListbox as (<T extends object = object>(props: TListboxProps<T>) => JSX.Element) & {
  displayName: string
}

Listbox.displayName = 'Listbox'
